import { useState } from 'react'
import type { LoaderFunctionArgs } from 'react-router-dom'
import { useLoaderData, useNavigate, useRevalidator, Link } from 'react-router-dom'
import { getProject, deleteProject } from '@/api/projects'
import { getProjectTimeline } from '@/api/memory'
import type { Project } from '@/types/project'
import type { L3Event } from '@/types/memory'
import { RepoStatus } from '@/components/project/RepoStatus'
import { RepoTreePanel } from '@/components/project/RepoTreePanel'
import { ChatList } from '@/components/project/ChatList'
import { ProjectExpertManager } from '@/components/project/ProjectExpertManager'
import { ProjectTimeline } from '@/components/project/ProjectTimeline'
import { ProjectMemoryPanel } from '@/components/project/ProjectMemoryPanel'
import { EditProjectModal } from '@/components/project/EditProjectModal'
import { TechStackEditor } from '@/components/project/TechStackEditor'
import { Button } from '@/components/ui/Button'

/**
 * PHASE 6 CORRECTION: ProjectTimeline is now wired in. This was
 * blocked in Phases 2-4 by an incorrect finding (documented and
 * corrected in HANDOFF.md's consolidated blocker list) that the
 * timeline endpoint didn't exist at all - re-checking main.go directly
 * showed it IS registered and reachable, just with hardcoded
 * limit=50/offset=0 and no pagination params. Fetched in parallel
 * with the project itself, consistent with the "parallel loader
 * fetch" pattern used everywhere else in this codebase (ChatPage,
 * ExpertsPage) - Promise.all rather than a sequential await, since
 * neither fetch depends on the other's result.
 */
async function loader({ params, request }: LoaderFunctionArgs) {
  const projectId = params.projectId as string
  const signal = request.signal

  const [project, timeline] = await Promise.all([
    getProject(projectId, { signal }),
    getProjectTimeline(projectId, { signal }),
  ])

  return { project, timeline }
}

export const projectRoute = { element: <ProjectPage />, loader }

export default function ProjectPage() {
  const { project, timeline } = useLoaderData() as { project: Project; timeline: L3Event[] }
  const revalidator = useRevalidator()
  const navigate = useNavigate()
  const [isEditOpen, setIsEditOpen] = useState(false)

  const handleDelete = async () => {
    // Feature #2 fix (docs bug list): no delete/archive option existed
    // anywhere in the UI, despite DELETE /projects/:id (soft delete)
    // being fully functional on the backend.
    if (!window.confirm(`Delete "${project.name}"? This cannot be undone from the UI.`)) return
    await deleteProject(project.id)
    navigate('/')
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <Link
        to="/"
        className="mb-3 inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
      >
        {'←'} Back to Projects
      </Link>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">{project.name}</h1>
          <p className="mt-1 text-sm text-text-secondary">{project.description}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setIsEditOpen(true)}>
            Edit
          </Button>
          <Button variant="danger" size="sm" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </div>

      <EditProjectModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        projectId={project.id}
        currentName={project.name}
        currentDescription={project.description}
        onSaved={() => revalidator.revalidate()}
      />

      <div className="mt-4">
        <p className="mb-1 text-sm font-medium text-text-secondary">Experts in this project</p>
        <ProjectExpertManager projectId={project.id} experts={project.experts} />
      </div>

      <div className="mt-6">
        <p className="mb-1 text-sm font-medium text-text-secondary">Tech Stack</p>
        <TechStackEditor
          projectId={project.id}
          // Project.techStack is string[] | Record<string,any> (legacy rows
          // stored an object); the editor only understands the array form.
          techStack={Array.isArray(project.techStack) ? project.techStack : undefined}
          onUpdated={() => revalidator.revalidate()}
        />
      </div>

      <div className="mt-6">
        <div className="max-h-72 overflow-y-auto rounded-md border border-surface-border p-1">
          <ChatList projectId={project.id} />
        </div>
      </div>

      <div className="mt-6">
        <p className="mb-1 text-sm font-medium text-text-secondary">Cross-Expert Decisions</p>
        <div className="max-h-80 overflow-y-auto rounded-md border border-surface-border p-1">
          <ProjectMemoryPanel projectId={project.id} />
        </div>
      </div>

      <div className="mt-6">
        <p className="mb-1 text-sm font-medium text-text-secondary">Recent Activity</p>
        <div className="max-h-80 overflow-y-auto rounded-md border border-surface-border p-1">
          <ProjectTimeline events={timeline} />
        </div>
      </div>

      <div className="mt-6">
        <RepoStatus projectId={project.id} />
      </div>

      <div className="mt-6">
        <RepoTreePanel projectId={project.id} />
      </div>
    </div>
  )
}
